import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  LINK_CLIENT_MANAGER,
  UNLINK_CLIENT_MANAGER,
} from "../../../../utils/GraphQL/mutations";
import ClientSearchInput from "../../adminComponents/ClientSearchInput/ClientSearchInput";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";
import ManagesListModal from "./ManagesListModal";

const fullName = (m) =>
  `${m?.firstName || ""} ${m?.lastName || ""}`.trim() || "Unknown";

/*
 * `hostedInModal` adapts this for the client console's "Add manager" modal
 * WITHOUT forking the link flow — re-implementing "search a client, link it,
 * undo" in the rail would be a second definition of it, which is the exact
 * thing this V2 pass keeps deleting. It changes three things:
 *
 *  - the search opens on mount, because otherwise the modal's entire body is a
 *    "Set Manager" button and the user has to click the affordance they just
 *    clicked to get there;
 *  - the "Manages: N" row is suppressed. A client can have no manager and still
 *    BE one, and "Manages: 3" inside a modal titled "Add manager" is noise.
 *    Dropping it also means ManagesListModal can never open from inside another
 *    modal, which would put two BdrModals on the same z-index rung;
 *  - `onLinked` fires after a successful link so the host can close itself.
 *
 * The Edit page passes none of these and is unchanged.
 */
const ManagerLinkContainer = ({
  clientObj,
  onMutated,
  hostedInModal,
  onLinked,
}) => {
  const navigate = useNavigate();
  const { handleSnack, handleClose } = useSnackbar();
  const { confirm } = useConfirm();
  const [searchVisible, setSearchVisible] = useState(!!hostedInModal);
  const [loading, setLoading] = useState(false);
  const [managesModalOpen, setManagesModalOpen] = useState(false);

  const [linkMutation] = useMutation(LINK_CLIENT_MANAGER, {
    fetchPolicy: "network-only",
  });
  const [unlinkMutation] = useMutation(UNLINK_CLIENT_MANAGER, {
    fetchPolicy: "network-only",
  });

  const clientId = clientObj?._id;
  const manager = clientObj?.manager;
  const managedById = clientObj?.managedById;
  const managedClientsCount = clientObj?.managedClientsCount || 0;

  // Capture the pre-mutation manager state so an Undo can revert. Read at
  // call time (not memoized) so each Undo reflects the actual previous link.
  const captureSnapshot = () => ({
    managedById: managedById || null,
    manager: manager ? { ...manager } : null,
  });

  const performUndo = async (snapshot) => {
    setLoading(true);
    try {
      if (snapshot.managedById) {
        await linkMutation({
          variables: { clientId, managerId: snapshot.managedById },
        });
      } else {
        await unlinkMutation({ variables: { clientId } });
      }
      if (onMutated) await onMutated();
      handleSnack("Reverted", 2500);
    } catch (err) {
      handleSnack(`Undo failed: ${err.message || err}`, 4000);
    } finally {
      setLoading(false);
    }
  };

  // 4-second snackbar with an Undo affordance — covers the "wrong client
  // picked via arrow-enter" failure mode without forcing a confirm modal
  // on the common (correct) path.
  const showLinkSuccessSnack = (verb, newManager, snapshot) => {
    handleSnack(
      <div className="manager-link-snack">
        <span>
          {verb} {fullName(newManager)}
        </span>
        <button
          type="button"
          className="manager-link-snack-undo-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
            performUndo(snapshot);
          }}
        >
          Undo
        </button>
      </div>,
      4000
    );
  };

  const handleSelectFromSearch = async (selectedClient) => {
    if (!selectedClient?._id) return;
    const snapshot = captureSnapshot();
    setLoading(true);
    try {
      const { data } = await linkMutation({
        variables: { clientId, managerId: selectedClient._id },
      });
      const updated = data?.linkClientManager;
      if (!updated) throw new Error("No data returned from server");
      setSearchVisible(false);
      if (onMutated) await onMutated();
      const verb = snapshot.managedById ? "Manager changed to" : "Linked to";
      showLinkSuccessSnack(verb, updated.manager, snapshot);
      /* Fires AFTER the snack, which is global and outlives this component, so
         the host can unmount us without swallowing the Undo affordance. Without
         it an "Add manager" modal would sit open and re-render into its
         manager-IS-set state — name, change, and an unlink button the console
         deliberately does not offer. */
      if (onLinked) onLinked(updated);
    } catch (err) {
      handleSnack(`Link failed: ${err.message || err}`, 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkConfirm = async () => {
    const ok = await confirm({
      title: "Unlink manager?",
      message: `${fullName(manager)} will no longer be the manager for this client.`,
      confirmLabel: "Unlink",
      destructive: true,
    });
    if (!ok) return;
    doUnlink();
  };

  const doUnlink = async () => {
    setLoading(true);
    try {
      const { data } = await unlinkMutation({ variables: { clientId } });
      if (!data?.unlinkClientManager) {
        throw new Error("No data returned from server");
      }
      if (onMutated) await onMutated();
      handleSnack("Manager unlinked", 2500);
    } catch (err) {
      handleSnack(`Unlink failed: ${err.message || err}`, 4000);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateManager = () => {
    if (managedById) {
      navigate(`/bdr-office/clients/view-client/${managedById}`);
    }
  };

  return (
    <div className="manager-link-container">
      <div className="manager-link-row">
        {manager?._id ? (
          <>
            <button
              type="button"
              className="manager-link-name-btn"
              onClick={handleNavigateManager}
              disabled={loading}
              title="Open manager's page"
            >
              <span className="manager-link-label">Managed by:</span>
              <span className="manager-link-name">{fullName(manager)}</span>
            </button>
            <button
              type="button"
              className="manager-link-icon-btn"
              onClick={() => setSearchVisible((v) => !v)}
              disabled={loading}
              title="Change manager"
              aria-label="Change manager"
            >
              {loading ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                <SearchIcon sx={{ fontSize: 20, color: "#fff" }} />
              )}
            </button>
            <button
              type="button"
              className="manager-link-icon-btn"
              onClick={handleUnlinkConfirm}
              disabled={loading}
              title="Unlink manager"
              aria-label="Unlink manager"
            >
              <LinkOffIcon sx={{ fontSize: 20, color: "#fff" }} />
            </button>
          </>
        ) : (
          /* Suppressed in the modal. The search is already open there, so this
             button's only effect would be to CLOSE it and leave the modal body
             empty — a dead end reached by clicking the same affordance that
             opened the modal. */
          !hostedInModal && (
          <button
            type="button"
            className="manager-link-set-btn bdr-grad-admin-btn bdr-border-thin bdr-shadow-subtle"
            onClick={() => setSearchVisible((v) => !v)}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <>
                <PersonAddAlt1Icon sx={{ fontSize: 16, color: "#fff" }} />
                <span>Set Manager</span>
              </>
            )}
          </button>
          )
        )}
      </div>

      <div
        className={`manager-link-search-slot${searchVisible ? " open" : ""}`}
      >
        {searchVisible && (
          <ClientSearchInput
            forManagerOfClientId={clientId}
            returnResults={handleSelectFromSearch}
          />
        )}
      </div>

      {managedClientsCount > 0 && !hostedInModal && (
        <div className="manager-link-manages-row">
          <span className="manager-link-manages-text">
            Manages: <strong>{managedClientsCount}</strong>
          </span>
          <button
            type="button"
            className="manager-link-icon-btn"
            onClick={() => setManagesModalOpen(true)}
            title="View managed clients"
            aria-label="View managed clients"
          >
            <VisibilityIcon sx={{ fontSize: 20, color: "#fff" }} />
          </button>
        </div>
      )}

      {managesModalOpen && (
        <ManagesListModal
          managerId={clientId}
          managerName={fullName(clientObj)}
          onClose={() => setManagesModalOpen(false)}
          onChildUnlinked={async () => {
            if (onMutated) await onMutated();
          }}
        />
      )}
    </div>
  );
};

export default ManagerLinkContainer;
